// Equipment Rates upload template — the client-side sample workbook.
//
// THREE SYNC POINTS. Changing a header here means changing all three:
//   1. this file (the downloadable sample)
//   2. the backend parser, src/utils/helper.util.ts parseEquipmentRatesFileToJson
//      (locates the header row by the 'Equipment Name' cell, maps BY HEADER TEXT)
//   3. the backend export builder,
//      src/machinery-equipment/helpers/equipment-rates-export.helper.ts
//
// EFFECTIVE-DATED: rows are matched to EXISTING assets (Assets > Asset Master)
// by Equipment Code first, then Equipment Name. Each changed row INSERTS a new
// rate version effective from the moment the upload is committed — history is
// never rewritten. An unmatched row is an error, not a create: machines are
// created in the Asset Master, this sheet only prices them.
//
// PERCENTAGES ARE WHOLE NUMBERS here (10 means 10%). The parser divides by 100.
// Diesel/Electricity C1 and C2 are plain multipliers and are NOT percentages —
// do not add a "%" to their headers or someone will eventually divide them.
//
// Cost is in LAKHS, matching the source workbook and users' data-entry habit.

export const EQUIPMENT_RATE_TEMPLATE_HEADERS = [
  "Equipment Name",
  "Equipment Code",
  "Cost of Equipment (Lacs)",
  "Scrap Value (%)",
  "Life (Hours)",
  "Life (Years)",
  "Standard Output per Hour",
  "Output UOM",
  "Insurance (% per Year)",
  "Interest Rate (%)",
  "Repair (% of Cost)",
  "Sundries (% of Repair)",
  "BHP Rating",
  "Diesel C1",
  "Diesel C2",
  "Diesel Effective Usage (%)",
  "Lubricant (% of Fuel)",
  "Total Power (kW)",
  "Electricity C1",
  "Electricity C2",
  "Electricity Effective Usage (%)",
  "Lubricant (% of Electricity)",
  "No. of Shifts",
  "Indirect Skilled (%)",
  "Indirect Unskilled (%)",
  "Foreman Nos/Shift",
  "Operator Nos/Shift",
  "Mechanic Nos/Shift",
  "Electrician Nos/Shift",
  "Supervisor Nos/Shift",
  "Driver Nos/Shift",
  "Cableman Nos/Shift",
  "Helper Nos/Shift",
  "Watchman Nos/Shift",
  "Beldar Nos/Shift",
];

export const EQUIPMENT_RATE_TEMPLATE_COL_WIDTHS = [
  { wch: 34 },
  { wch: 16 },
  ...new Array(EQUIPMENT_RATE_TEMPLATE_HEADERS.length - 2).fill({ wch: 15 }),
];

/**
 * Two real machines from the source workbook, so the sample teaches the
 * conventions rather than showing round made-up numbers:
 *  - Drill Jumbo: diesel AND electric, a full crew, 10% diesel effective usage
 *  - Ventilation Fan 45 Kw: electric only, no diesel arm at all
 * Equipment Code left blank in the sample — fill it with the asset's code
 * (AST-0001 style) for an exact match.
 */
export const EQUIPMENT_RATE_TEMPLATE_SAMPLE_ROWS = [
  [
    "Drill Jumbo",
    "",
    435, // Cost (Lacs)
    10, // Scrap %
    15000, // Life hours
    10, // Life years
    50, // Standard output per hour — a PLAIN rate, not a percentage
    "Cum", // Output UOM
    0.5, // Insurance % per year
    9.5, // Interest %
    120, // Repair % of cost
    5, // Sundries % of repair
    247.99, // BHP
    0.7, // Diesel C1
    0.7, // Diesel C2
    10, // Diesel effective usage %
    10, // Lubricant % of fuel
    185, // Total power kW
    0.8, // Electricity C1
    1, // Electricity C2
    100, // Electricity effective usage %
    10, // Lubricant % of electricity
    1, // Shifts
    0, // Indirect skilled %
    0, // Indirect unskilled %
    0.125, // Foreman
    2, // Operator
    0.25, // Mechanic
    0, // Electrician
    0, // Supervisor
    0, // Driver
    0, // Cableman
    4, // Helper
    0.25, // Watchman
    0, // Beldar
  ],
  [
    "Ventilation Fan 45 Kw",
    "",
    21.05,
    10,
    58000,
    12,
    "", // a fan has no meaningful output rate — blank leaves Rs/Unit empty
    "",
    "", // no insurance recorded for the fans
    9.5,
    100,
    5,
    "", // no diesel arm
    "",
    "",
    "",
    "",
    45, // kW
    0.7,
    1,
    100,
    10,
    1,
    0,
    0,
    0,
    0,
    0,
    0.25,
    0,
    0,
    0,
    0,
    0,
    0,
  ],
];

export const EQUIPMENT_RATE_TEMPLATE_INSTRUCTIONS = [
  ["Equipment Rates — Upload Instructions"],
  [""],
  ["Equipment Name", "REQUIRED. Rows are matched to EXISTING machines in Assets > Asset Master — by Equipment Code first, then by name (ignoring case and extra spaces). A machine that does not exist there is a row ERROR: create it in the Asset Master first, then re-upload."],
  ["Equipment Code", "The asset's code (e.g. AST-0012). Fill it when two machines share a name — it makes the match exact."],
  ["Everything else", "Optional. A blank percentage inherits the scope default from the Rate Parameters card."],
  [""],
  ["Effective date", "Rates are EFFECTIVE-DATED. Each changed row becomes a NEW rate version effective from the moment you commit this upload — earlier hours stay costed on the old version. Unchanged rows add nothing."],
  [""],
  ["Percentages", "Enter WHOLE NUMBERS: 10 means 10%, 120 means 120%. Do not enter 0.10."],
  ["C1 / C2", "Plain multipliers, NOT percentages. Enter 0.7, not 70."],
  ["Cost of Equipment", "In LAKHS. 435 means Rs 4,35,00,000."],
  [""],
  ["Standard Output per Hour", "OPTIONAL. The machine's nominal output rate as a PLAIN number — 50 means 50 units per hour, not 50%. It is the denominator behind the Rs/Unit columns on the export and the Rs/Cum toggle in the app. Leave it blank and those simply stay empty; nothing else is affected."],
  ["Output UOM", "The unit that Standard Output is measured in, e.g. Cum. Matched to the UOM master by name or short code (Cum, cu.m, m3 all work). The '/Hr' is implied — do not type it."],
  ["Equipment Norms differ", "Inside an Equipment (Deployment) Norm each machine has its OWN production norm plus utilisation factors, and the task and report screens divide by that instead. This column is the rate master's own standalone basis; the two figures are not interchangeable."],
  [""],
  ["What is NOT in this sheet", "Diesel price, electricity rate and the ten crew wages. Those live on the shared Rate Parameters card (also effective-dated) so that one edit re-rates the whole fleet from its date."],
  ["Computed columns", "Depreciation, Interest, Repair, Misc, Insurance, Electricity, Fuel, Lubricants and Crew are CALCULATED from these inputs. They appear on the export and are ignored if you upload them back."],
  [""],
  ["Missing life data", "A machine with no Life (Hours) or Life (Years) still imports; its ownership costs show blank with a warning."],
];
